/**
 * FEEDBACK WIDGET — DIALOG D'ENVOI (TICKET-CRM-14)
 * Formulaire RHF + Zod -> Edge Function create-feedback-issue -> issue GitHub.
 * Le snapshot debug est figé à l'OUVERTURE du dialog (pas au submit) pour ne
 * pas capturer les interactions avec le formulaire lui-même.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Form, FormInput, FormTextarea, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useSupabase } from '../../hooks/useSupabase';
import { feedbackFormSchema, type FeedbackFormValues } from '../../schemas/feedback';
import type { DebugSnapshot, FeedbackTicketResponse } from '../../lib/feedback/feedback.types';
import { debugCaptureService } from '../../lib/feedback/debug-capture';
import { FeedbackTypeSelector } from './FeedbackTypeSelector';
import { DebugDataPreview } from './DebugDataPreview';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const supabase = useSupabase();
  const [sending, setSending] = useState(false);
  const [snapshot, setSnapshot] = useState<DebugSnapshot | null>(null);
  const [success, setSuccess] = useState<FeedbackTicketResponse | null>(null);

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: { type: 'bug', title: '', description: '' },
  });

  // Fige le snapshot et réinitialise l'état à chaque ouverture.
  useEffect(() => {
    if (open) {
      setSnapshot(debugCaptureService.captureSnapshot());
      setSuccess(null);
      form.reset({ type: 'bug', title: '', description: '' });
    }
  }, [open, form]);

  async function handleSubmit(values: FeedbackFormValues) {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-feedback-issue', {
        body: {
          type: values.type,
          title: values.title,
          description: values.description,
          debugSnapshot: snapshot ?? undefined,
        },
      });

      if (error) {
        toast.error("Impossible d'envoyer le ticket. Réessayez ou contactez le support.");
        return;
      }

      const response = data as FeedbackTicketResponse;
      setSuccess(response);
      toast.success(`Ticket #${response.issueNumber} créé`);
    } finally {
      setSending(false);
    }
  }

  const type = form.watch('type');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {success ? (
          <>
            <DialogHeader>
              <DialogTitle>Ticket créé</DialogTitle>
              <DialogDescription>
                Votre signalement #{success.issueNumber} a bien été transmis à l'équipe.
              </DialogDescription>
            </DialogHeader>
            <div className="py-6 text-center">
              <CheckCircle className="mx-auto mb-4 h-12 w-12 text-green-500" />
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Fermer
                </Button>
                <Button asChild>
                  <a href={success.issueUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Voir sur GitHub
                  </a>
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Signaler un problème</DialogTitle>
              <DialogDescription>
                Signalez un bug ou proposez une amélioration. Les informations de debug sont jointes
                automatiquement.
              </DialogDescription>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <div className="max-h-96 space-y-4 overflow-y-auto pr-2">
                  <FeedbackTypeSelector
                    value={type}
                    onChange={(value) => form.setValue('type', value)}
                    disabled={sending}
                  />
                  <FormInput
                    control={form.control}
                    name="title"
                    label="Titre"
                    placeholder="Résumé du problème en une phrase"
                    required
                    disabled={sending}
                  />
                  <FormTextarea
                    control={form.control}
                    name="description"
                    label="Description"
                    placeholder="Ce que vous faisiez, ce qui s'est passé, ce que vous attendiez…"
                    rows={5}
                    required
                    disabled={sending}
                  />
                  <DebugDataPreview snapshot={snapshot} />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={sending}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={sending}>
                    {sending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Envoi…
                      </>
                    ) : (
                      'Envoyer'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
